export interface TourProgress {
  version: 1;
  status: 'in_progress' | 'completed' | 'skipped';
  stepId: string;
  viewedSteps: string[];
  /** Features dismissed explicitly; never includes features the viewer cannot access. */
  dismissedSteps?: string[];
  /** Snapshot of this run, so a reload can resume even after permissions change. */
  runSteps?: string[];
  startedAt: number;
  updatedAt: number;
  finishedAt: number | null;
  runs: number;
}

export const tourStorageKey = (id: string) => `argus.panel-tour.v1.${id}`;
// Keep dismissal working for the current session when storage is unavailable.
const sessionProgress = new Map<string, TourProgress>();

export function parseTourProgress(raw: string | null): TourProgress | null {
  try {
    const value = JSON.parse(raw ?? 'null');
    if (
      !value ||
      value.version !== 1 ||
      !['in_progress', 'completed', 'skipped'].includes(value.status) ||
      typeof value.stepId !== 'string' ||
      !Array.isArray(value.viewedSteps) ||
      !value.viewedSteps.every((step: unknown) => typeof step === 'string') ||
      ![value.dismissedSteps, value.runSteps].every((steps) =>
        steps === undefined || (Array.isArray(steps) && steps.every((id: unknown) => typeof id === 'string'))) ||
      !Number.isFinite(value.startedAt) ||
      !Number.isFinite(value.updatedAt) ||
      !(value.finishedAt === null || Number.isFinite(value.finishedAt)) ||
      !Number.isInteger(value.runs) ||
      value.runs < 1
    )
      return null;
    return value as TourProgress;
  } catch {
    return null;
  }
}

export function readTourProgress(id: string): TourProgress | null {
  try {
    return (
      parseTourProgress(localStorage.getItem(tourStorageKey(id))) ??
      sessionProgress.get(id) ??
      null
    );
  } catch {
    return sessionProgress.get(id) ?? null;
  }
}

export function saveTourProgress(id: string, progress: TourProgress) {
  sessionProgress.set(id, progress);
  try {
    localStorage.setItem(tourStorageKey(id), JSON.stringify(progress));
  } catch {
    /* Private browsing / storage limits must never block Skip. */
  }
}

export function startTourProgress(
  stepId: string,
  previous: TourProgress | null,
  replay: boolean,
  now = Date.now(),
): TourProgress {
  if (!replay && previous?.status === 'in_progress')
    return { ...previous, stepId, updatedAt: now };
  return {
    version: 1,
    status: 'in_progress',
    stepId,
    viewedSteps: Array.from(new Set([...(previous?.viewedSteps ?? []), stepId])),
    dismissedSteps: previous?.dismissedSteps ?? [],
    startedAt: now,
    updatedAt: now,
    finishedAt: null,
    runs: (previous?.runs ?? 0) + 1,
  };
}

/** Only currently accessible features participate in automatic onboarding. */
export function pendingTourSteps(available: string[], previous: TourProgress | null): string[] {
  if (!previous) return available;
  if (previous.status === 'in_progress' && previous.runSteps?.some((id) => available.includes(id)))
    return previous.runSteps.filter((id) => available.includes(id));
  const known = new Set([...previous.viewedSteps, ...(previous.dismissedSteps ?? [])]);
  return available.filter((id) => !known.has(id));
}

export function finishTourProgress(progress: TourProgress, status: 'completed' | 'skipped', accessibleRun: string[], now = Date.now()): TourProgress {
  return {
    ...progress, status, updatedAt: now, finishedAt: now,
    dismissedSteps: Array.from(new Set([...(progress.dismissedSteps ?? []), ...accessibleRun])),
    runSteps: [],
  };
}

export function advanceTourProgress(
  progress: TourProgress,
  stepId: string,
  now = Date.now(),
): TourProgress {
  return {
    ...progress,
    stepId,
    viewedSteps: Array.from(new Set([...progress.viewedSteps, stepId])),
    updatedAt: now,
  };
}
